import { Avatar } from '@assembly-js/design-system';
import { Skeleton } from '@mui/material';
import React from 'react';
import { shallowEqual } from 'react-redux';

import { SidebarLogo } from 'src/components/sidebar/SidebarLogo';
import { AppViewMode } from 'src/constants';
import {
  PortalConfigContext,
  SidebarThemeContext,
  useFeatureFlags,
} from 'src/context';
import { WorkspaceSelector } from 'src/features/sidebar/WorkspaceSelector';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';
import { useAppSelector } from 'src/hooks/useStore';
import { useWorkspaces } from 'src/hooks/useWorkspaces';
import Logo from 'src/legacy/components/Logo';
import { AuthenticatedPortalDataType } from 'src/legacy/components/Onboarding/Login/onboardingLoginTypes';
import { useGetPortalConfigQuery } from 'src/services/api';
import { RootState } from 'src/store';
import { Company, selectAllCompaniesMap } from 'src/store/clients/types';
import { selectCurrentUser } from 'src/store/storeUtils';
import { getInitials } from 'src/utils/UserUtils';

import { SidebarLogoMenu } from './SidebarLogoMenu';

type Props = {
  isSettingsPage: boolean;
};

type WorkspacesMap = Record<
  string,
  Record<string, AuthenticatedPortalDataType>
>;

const useWorkspacesQuery = (options?: { enabled?: boolean }) => {
  const [workspaces, setWorkspaces] = React.useState<WorkspacesMap>({});
  const { getWorkspaces, loadingWorkspaces } = useWorkspaces();
  const hasFetched = React.useRef(false);

  React.useEffect(() => {
    if (!options?.enabled || hasFetched.current) return;
    hasFetched.current = true;

    void getWorkspaces().then((data) => {
      setWorkspaces(data.workspaces);
    });
  }, [getWorkspaces, options?.enabled]);

  return { workspaces, isLoading: loadingWorkspaces };
};

export const SidebarLogoContainer = ({ isSettingsPage }: Props) => {
  const { EnableNewSidebar } = useFeatureFlags();
  const { sidebarTextColor } = React.useContext(SidebarThemeContext);
  const { backgroundColor: badgeBackgroundColor, textColor: badgeTextColor } =
    useNotificationBadgeData(true);
  const portalConfig = React.useContext(PortalConfigContext);
  const { data: config, isLoading: isPortalConfigLoading } =
    useGetPortalConfigQuery();
  const { user, isClient } = useAppSelector((state: RootState) => ({
    user: state.user,
    isClient: state.user.isClient,
  }));

  // Get current user and companies map from Redux store
  const currentUser = useAppSelector(selectCurrentUser);
  const allCompaniesMap = useAppSelector(selectAllCompaniesMap);

  // Get user companies using efficient map lookup for multi-company support
  const userCompanies = React.useMemo(() => {
    if (!currentUser || !isClient || !currentUser.fields.companyIds) {
      return [];
    }

    return currentUser.fields.companyIds
      .map((companyId) => allCompaniesMap.get(companyId))
      .filter((company): company is Company => company !== undefined);
  }, [currentUser, isClient, allCompaniesMap]);

  const companies = userCompanies ?? [];
  const activeClientCompanyId = useAppSelector(
    (state) => state.activeCompany.activeCompanyId,
    shallowEqual,
  );

  const { workspaces } = useWorkspacesQuery({
    // only fetch workspaces for IUs
    enabled: portalConfig.viewMode === AppViewMode.INTERNAL,
  });

  const isCompaniesEnabled = !config?.structFields?.disableCompanies;

  const showDefaultLogo =
    portalConfig.viewMode === AppViewMode.INTERNAL || // Internal users see the default logo
    !isCompaniesEnabled || // If companies are not enabled, the default logo will be shown
    !companies.length || // If user is not associated with any companies, the default logo will be shown
    !portalConfig?.brand?.allowClientBranding; // No branding if feature isn't turned on

  const getLogoProps = () => {
    const userCompanyId = activeClientCompanyId;
    const company = companies.find((x) => x.id === userCompanyId); // get company details using company id
    return {
      logo: company?.fields?.avatarImageURL ?? '',
      name: company?.fields?.name ?? '',
      fallbackColor: company?.fields?.fallbackColor ?? '',
      isPlaceHolderCompany: company?.fields?.isPlaceholder ?? false,
    };
  };

  const { logo, name, isPlaceHolderCompany, fallbackColor } = getLogoProps();

  // show nothing when it is settings page, or users or companies are not loaded yet.
  if (isSettingsPage) {
    return null;
  }

  // show skeleton while loading portal config or users
  if (isPortalConfigLoading) {
    return <Skeleton variant="rectangular" width="100%" height={28} />;
  }

  if (EnableNewSidebar) {
    // For internal users (and any case where showDefaultLogo is true), the workspace
    // icon comes from portalConfig.assets.logo.iconImageUrl — the same source the
    // legacy <Logo /> component used. For branded client portals, use the company logo.
    const switcherIconSrc =
      showDefaultLogo || isPlaceHolderCompany
        ? portalConfig.assets.logo.iconImageUrl || undefined
        : logo || undefined;

    const switcherName =
      showDefaultLogo || isPlaceHolderCompany
        ? portalConfig.name
        : name || portalConfig.name;

    // The branded company switcher is only shown for a real client company;
    // the default portal workspace (internal users, placeholder, or branding
    // off) is not the company switcher.
    const isCompanySwitcher = !showDefaultLogo && !isPlaceHolderCompany;

    const hasCompanyFallbackColor = isCompanySwitcher && Boolean(fallbackColor);
    const shouldUseNeutralClientFallback =
      isClient && !isCompanySwitcher && !switcherIconSrc;
    const fallbackAvatarStyle = hasCompanyFallbackColor
      ? { border: 'none' }
      : {
          backgroundColor: badgeBackgroundColor,
          color: badgeTextColor,
          border: 'none',
        };
    const companyFallbackAvatar =
      !switcherIconSrc && !shouldUseNeutralClientFallback ? (
        <Avatar
          alt={switcherName}
          src=""
          text={getInitials(switcherName)}
          fallbackColor={hasCompanyFallbackColor ? fallbackColor : undefined}
          style={fallbackAvatarStyle}
          size="sm"
          variant="rounded"
          className="mr-2"
        />
      ) : undefined;

    return (
      <WorkspaceSelector
        workspaces={workspaces}
        iconSrc={switcherIconSrc}
        iconFallbackColor={hasCompanyFallbackColor ? fallbackColor : undefined}
        iconSize={shouldUseNeutralClientFallback ? 24 : undefined}
        iconSlot={companyFallbackAvatar}
        name={switcherName}
        userIsClient={isClient}
        isTestUser={user.data?.isTestUser ?? false}
      />
    );
  }

  return (
    <SidebarLogoMenu workspaces={workspaces}>
      {showDefaultLogo || isPlaceHolderCompany ? (
        <Logo />
      ) : (
        <SidebarLogo
          iconFallbackBGColor={fallbackColor}
          sidebarTextColor={sidebarTextColor}
          workspaceLogoURL={logo && !isPlaceHolderCompany ? logo : undefined}
          workspaceName={name}
        />
      )}
    </SidebarLogoMenu>
  );
};
